import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  SimpleChange,
  TemplateRef,
} from '@angular/core';

import { Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BillingPlanTier } from '@recruitee/products-types';
import { IconToken } from '@recruitee/user-interface';

import { FeatureGuardFacade } from '../feature-guard.facade';
import { FeatureGuardComponent, FeatureGuardMode, GuardedFeature } from '../feature-guard.types';

@Component({
  selector: 'rt-feature-guard-button',
  templateUrl: 'feature-guard-button.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeatureGuardButtonComponent implements FeatureGuardComponent {
  @Input({ required: true }) public feature: GuardedFeature;
  @Input() public forceEnabled: boolean;
  @Input() public mode: FeatureGuardMode = FeatureGuardMode.Dialog;
  @Input() public customTpl: TemplateRef<any>;
  @Input() public defaultBtnText: string;

  public isFeatureAvailable: boolean = true;
  public colorToken: IconToken;
  public GuardedFeature: typeof GuardedFeature = GuardedFeature;

  private readonly billingPlanTokenRecord: Record<BillingPlanTier, IconToken> = {
    [BillingPlanTier.Launch]: 'colorIconPrimaryDefault',
    [BillingPlanTier.Scale]: 'colorIconScaleDefault',
    [BillingPlanTier.Lead]: 'colorIconLeadDefault',
    [BillingPlanTier.Start]: 'colorIconPrimaryDefault',
    [BillingPlanTier.Grow]: 'colorIconScaleDefault',
    [BillingPlanTier.Optimize]: 'colorIconLeadDefault',
  };
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    public facade: FeatureGuardFacade,
    protected cdRef: ChangeDetectorRef,
  ) {}

  public ngOnInit() {
    if (this.forceEnabled) {
      this.isFeatureAvailable = this.forceEnabled;
    } else {
      const featureAvailable$ = this.facade.checkFeatureAvailability(this.feature);
      const availableFromPlan$ = this.facade.getPlanWhereFeatureIsAvailable(this.feature);

      combineLatest([featureAvailable$, availableFromPlan$])
        .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
        .subscribe(([isFeatureAvailable, availableFromPlan]) => {
          this.isFeatureAvailable = isFeatureAvailable;
          this.colorToken = this.billingPlanTokenRecord[availableFromPlan!];
          this.cdRef.detectChanges();
        });
    }
  }

  public ngOnChanges({ forceEnabled }: { forceEnabled: SimpleChange }) {
    if (forceEnabled) {
      this.isFeatureAvailable = this.forceEnabled;
      this.cdRef.detectChanges();
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
